(() => {
  'use strict';

  const controllers = new WeakMap();
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  const pad = (value) => String(value).padStart(2, '0');

  const normalizeIndex = (index, length) => {
    if (!length) return 0;
    return ((index % length) + length) % length;
  };

  const buildXShareUrl = ({ title, url, hashtag }) => {
    const encodedTitle = encodeURIComponent(title || '');
    const encodedUrl = encodeURIComponent(url || '');

    let shareUrl = `https://x.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;

    if (hashtag) {
      const normalizedHashtag = String(hashtag).replace(/^#/, '').trim();
      if (normalizedHashtag) {
        shareUrl += `&hashtags=${encodeURIComponent(normalizedHashtag)}`;
      }
    }

    return shareUrl;
  };

  const buildLineShareUrl = ({ title, url, hashtag }) => {
    let text = title || '';

    if (url) {
      text += ` ${url}`;
    }

    if (hashtag) {
      const normalizedHashtag = String(hashtag).replace(/^#/, '').trim();
      if (normalizedHashtag) {
        text += ` #${normalizedHashtag}`;
      }
    }

    return `https://line.me/R/share?text=${encodeURIComponent(text.trim())}`;
  };

  const createController = (section) => {
    if (!section || controllers.has(section)) return;

    const slider = section.querySelector('[data-dotd-digital-slider]');
    const viewport = slider?.querySelector('[data-dotd-digital-viewport]');
    const track = slider?.querySelector('[data-dotd-digital-track]');

    const originalSlides = [
      ...(slider?.querySelectorAll(
        '[data-dotd-digital-slide]:not([data-dotd-digital-clone])',
      ) || []),
    ];

    const previousButton = slider?.querySelector(
      '[data-dotd-digital-prev]',
    );

    const nextButton = slider?.querySelector(
      '[data-dotd-digital-next]',
    );

    const counter = slider?.querySelector(
      '[data-dotd-digital-counter]',
    );

    const pagination = slider?.querySelector(
      '[data-dotd-digital-pagination]',
    );

    const modal = section.querySelector(
      '[data-dotd-digital-modal]',
    );

    const modalTitle = modal?.querySelector(
      '[data-dotd-digital-modal-title]',
    );

    const modalKicker = modal?.querySelector(
      '[data-dotd-digital-modal-kicker]',
    );

    const modalPreview = modal?.querySelector(
      '[data-dotd-digital-modal-preview]',
    );

    const smartphoneLink = modal?.querySelector(
      '[data-dotd-digital-smartphone]',
    );

    const desktopLink = modal?.querySelector(
      '[data-dotd-digital-desktop]',
    );

    const modalShare = modal?.querySelector(
      '[data-dotd-digital-modal-share]',
    );

    const shareRoot = modalShare?.querySelector(
      '[data-dotd-share]',
    );

    const shareXLink = shareRoot?.querySelector(
      '[data-share-channel="x"]',
    );

    const shareLineLink = shareRoot?.querySelector(
      '[data-share-channel="line"]',
    );

    const shareCopyButton = shareRoot?.querySelector(
      '[data-dotd-share-copy]',
    );

    const shareStatus = shareRoot?.querySelector(
      '[data-dotd-share-status]',
    );

    const closeButtons = [
      ...(modal?.querySelectorAll(
        '[data-dotd-digital-close]',
      ) || []),
    ];

    const slideCount = originalSlides.length;
    const loopEnabled = slideCount > 1;

    let renderedSlides = [...originalSlides];
    let firstClone = null;
    let lastClone = null;

    let currentIndex = 0;
    let currentRenderedIndex = loopEnabled ? 1 : 0;

    let pendingDesktopJump = null;
    let scrollFrame = null;
    let mobileSettleTimer = null;
    let activeTrigger = null;
    let openedFromShareHash = false;
    let destroyed = false;

    const cleanups = [];
    const dots = [];

    const listen = (
      target,
      eventName,
      handler,
      options,
    ) => {
      if (!target?.addEventListener) return;

      target.addEventListener(
        eventName,
        handler,
        options,
      );

      cleanups.push(() => {
        target.removeEventListener(
          eventName,
          handler,
          options,
        );
      });
    };

    const prepareClone = (clone, position) => {
      clone.setAttribute(
        'data-dotd-digital-clone',
        position,
      );

      clone.setAttribute('aria-hidden', 'true');

      clone
        .querySelectorAll(
          'a, button, input, select, textarea, [tabindex]',
        )
        .forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });

      clone.removeAttribute('id');

      clone
        .querySelectorAll('[id]')
        .forEach((element) => {
          element.removeAttribute('id');
        });

      return clone;
    };

    const createLoopClones = () => {
      if (
        !loopEnabled ||
        !track ||
        !originalSlides.length
      ) {
        renderedSlides = [...originalSlides];
        return;
      }

      firstClone = prepareClone(
        originalSlides[0].cloneNode(true),
        'after',
      );

      lastClone = prepareClone(
        originalSlides[
          originalSlides.length - 1
        ].cloneNode(true),
        'before',
      );

      track.prepend(lastClone);
      track.append(firstClone);

      renderedSlides = [
        ...track.querySelectorAll(
          '[data-dotd-digital-slide]',
        ),
      ];
    };

    createLoopClones();

    const updateControls = () => {
      if (previousButton) {
        previousButton.disabled = slideCount <= 1;
      }

      if (nextButton) {
        nextButton.disabled = slideCount <= 1;
      }
    };

    const updateInterface = (index) => {
      if (!slideCount) return;

      currentIndex = normalizeIndex(
        index,
        slideCount,
      );

      if (counter) {
        counter.textContent =
          `${pad(currentIndex + 1)} / ` +
          `${pad(slideCount)}`;
      }

      dots.forEach((dot, dotIndex) => {
        const isActive =
          dotIndex === currentIndex;

        dot.classList.toggle(
          'is-active',
          isActive,
        );

        if (isActive) {
          dot.setAttribute(
            'aria-current',
            'true',
          );
        } else {
          dot.removeAttribute(
            'aria-current',
          );
        }
      });

      updateControls();
    };

    const setTrackPosition = (
      renderedIndex,
      animate = true,
    ) => {
      if (!track) return;

      currentRenderedIndex = renderedIndex;

      if (!animate) {
        track.style.transition = 'none';
      } else {
        track.style.removeProperty(
          'transition',
        );
      }

      track.style.transform =
        `translate3d(-${renderedIndex * 100}%, 0, 0)`;

      if (!animate) {
        track.getBoundingClientRect();

        window.requestAnimationFrame(() => {
          if (!destroyed) {
            track.style.removeProperty(
              'transition',
            );
          }
        });
      }
    };

    const getMobileLeft = (renderedIndex) => {
      const slide =
        renderedSlides[renderedIndex];

      if (!slide) return 0;

      return slide.offsetLeft;
    };

    const scrollToRendered = (
      renderedIndex,
      smooth = true,
    ) => {
      if (!viewport) return;

      currentRenderedIndex = renderedIndex;

      viewport.scrollTo({
        left: getMobileLeft(renderedIndex),
        behavior: smooth ? 'smooth' : 'auto',
      });
    };

    const jumpMobileToRealSlide = (
      renderedIndex,
    ) => {
      if (!viewport) return;

      window.requestAnimationFrame(() => {
        scrollToRendered(
          renderedIndex,
          false,
        );
      });
    };

    const getRenderedIndexForLogical = (
      logicalIndex,
    ) => {
      if (!loopEnabled) {
        return logicalIndex;
      }

      return logicalIndex + 1;
    };

    const moveTo = (
      requestedIndex,
      options = {},
    ) => {
      if (!slideCount) return;

      const {
        smooth = true,
        direction = 0,
      } = options;

      const normalizedIndex =
        normalizeIndex(
          requestedIndex,
          slideCount,
        );

      let targetRenderedIndex =
        getRenderedIndexForLogical(
          normalizedIndex,
        );

      const movingForwardAcrossEnd =
        loopEnabled &&
        direction > 0 &&
        currentIndex === slideCount - 1 &&
        normalizedIndex === 0;

      const movingBackwardAcrossStart =
        loopEnabled &&
        direction < 0 &&
        currentIndex === 0 &&
        normalizedIndex === slideCount - 1;

      if (movingForwardAcrossEnd) {
        targetRenderedIndex =
          renderedSlides.length - 1;
      }

      if (movingBackwardAcrossStart) {
        targetRenderedIndex = 0;
      }

      updateInterface(normalizedIndex);

      if (mobileQuery.matches) {
        scrollToRendered(
          targetRenderedIndex,
          smooth,
        );

        return;
      }

      if (movingForwardAcrossEnd) {
        pendingDesktopJump = 1;
      } else if (movingBackwardAcrossStart) {
        pendingDesktopJump = slideCount;
      } else {
        pendingDesktopJump = null;
      }

      setTrackPosition(
        targetRenderedIndex,
        smooth,
      );
    };

    const moveBy = (direction) => {
      moveTo(
        currentIndex + direction,
        {
          direction,
          smooth: true,
        },
      );
    };

    if (pagination && slideCount) {
      pagination.replaceChildren();

      originalSlides.forEach(
        (_, index) => {
          const dot =
            document.createElement('button');

          dot.type = 'button';

          dot.setAttribute(
            'aria-label',
            `${index + 1}枚目を表示`,
          );

          listen(
            dot,
            'click',
            () => {
              moveTo(index, {
                smooth: true,
              });
            },
          );

          pagination.appendChild(dot);
          dots.push(dot);
        },
      );
    }

    const correctMobileLoopPosition = () => {
      if (
        !mobileQuery.matches ||
        !loopEnabled ||
        !viewport
      ) {
        return;
      }

      if (currentRenderedIndex === 0) {
        currentRenderedIndex = slideCount;

        jumpMobileToRealSlide(
          slideCount,
        );

        return;
      }

      if (
        currentRenderedIndex ===
        renderedSlides.length - 1
      ) {
        currentRenderedIndex = 1;

        jumpMobileToRealSlide(1);
      }
    };

    const scheduleMobileCorrection = () => {
      if (mobileSettleTimer !== null) {
        window.clearTimeout(
          mobileSettleTimer,
        );
      }

      mobileSettleTimer =
        window.setTimeout(() => {
          mobileSettleTimer = null;
          correctMobileLoopPosition();
        }, 140);
    };

    const updateMobileIndex = () => {
      scrollFrame = null;

      if (
        !mobileQuery.matches ||
        !viewport ||
        !renderedSlides.length
      ) {
        return;
      }

      const viewportStart =
        viewport.scrollLeft;

      let nearestRenderedIndex = 0;
      let nearestDistance = Infinity;

      renderedSlides.forEach(
        (slide, renderedIndex) => {
          const distance = Math.abs(
            slide.offsetLeft -
            viewportStart,
          );

          if (
            distance <
            nearestDistance
          ) {
            nearestDistance = distance;
            nearestRenderedIndex =
              renderedIndex;
          }
        },
      );

      currentRenderedIndex =
        nearestRenderedIndex;

      let logicalIndex;

      if (!loopEnabled) {
        logicalIndex =
          nearestRenderedIndex;
      } else if (
        nearestRenderedIndex === 0
      ) {
        logicalIndex =
          slideCount - 1;
      } else if (
        nearestRenderedIndex ===
        renderedSlides.length - 1
      ) {
        logicalIndex = 0;
      } else {
        logicalIndex =
          nearestRenderedIndex - 1;
      }

      updateInterface(logicalIndex);
      scheduleMobileCorrection();
    };

    const requestMobileIndexUpdate = () => {
      if (scrollFrame !== null) return;

      scrollFrame =
        window.requestAnimationFrame(
          updateMobileIndex,
        );
    };

    const handleTrackTransitionEnd = (
      event,
    ) => {
      if (
        event.target !== track ||
        event.propertyName !== 'transform' ||
        pendingDesktopJump === null
      ) {
        return;
      }

      const jumpIndex =
        pendingDesktopJump;

      pendingDesktopJump = null;

      setTrackPosition(
        jumpIndex,
        false,
      );
    };

    const setDownloadState = (
      link,
      url,
    ) => {
      if (!link) return;

      const available = Boolean(url);

      link.hidden = !available;

      if (available) {
        link.href = url;
      } else {
        link.removeAttribute('href');
      }
    };

    const clearShareStatus = () => {
      if (!shareStatus) return;

      window.clearTimeout(
        shareStatus.dotdShareTimer,
      );

      shareStatus.textContent = '';
    };

    const updateModalShare = ({
      shareTitle = '',
      shareUrl = '',
      shareHashtag = '',
    }) => {
      if (!modalShare || !shareRoot) return;

      const hasShare = Boolean(shareUrl);
      modalShare.hidden = !hasShare;

      if (!hasShare) return;

      if (shareXLink) {
        shareXLink.href = buildXShareUrl({
          title: shareTitle,
          url: shareUrl,
          hashtag: shareHashtag,
        });
      }

      if (shareLineLink) {
        shareLineLink.href = buildLineShareUrl({
          title: shareTitle,
          url: shareUrl,
          hashtag: shareHashtag,
        });
      }

      if (shareCopyButton) {
        shareCopyButton.setAttribute(
          'data-share-url',
          shareUrl,
        );
      }

      clearShareStatus();
    };

    const isModalOpen = () =>
      Boolean(
        modal?.classList.contains(
          'is-open',
        ),
      );

    const closeModal = ({
      restoreFocus = true,
    } = {}) => {
      if (
        !modal ||
        !isModalOpen()
      ) {
        return;
      }

      modal.classList.remove('is-open');

      modal.setAttribute(
        'aria-hidden',
        'true',
      );

      document.body.classList.remove(
        'dotd-digital-modal-open',
      );

      const trigger = activeTrigger;
      const shouldClearHash = openedFromShareHash;

      activeTrigger = null;
      openedFromShareHash = false;

      if (shouldClearHash) {
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}${window.location.search}`,
        );
      }

      if (restoreFocus) {
        window.setTimeout(() => {
          trigger?.focus();
        }, 20);
      }
    };

    const openModal = (
      trigger,
      {
        fromShareHash = false,
      } = {},
    ) => {
      if (!modal || !trigger) return;

      activeTrigger = trigger;
      openedFromShareHash = fromShareHash;

      const {
        title = '',
        kicker = '',
        preview = '',
        smartphoneUrl = '',
        desktopUrl = '',
        shareTitle = '',
        shareUrl = '',
        shareHashtag = '',
      } = trigger.dataset;

      if (modalTitle) {
        modalTitle.textContent = title;
      }

      if (modalKicker) {
        modalKicker.textContent =
          kicker;
      }

      if (modalPreview) {
        modalPreview.src = preview;
        modalPreview.alt = title;
      }

      setDownloadState(
        smartphoneLink,
        smartphoneUrl,
      );

      setDownloadState(
        desktopLink,
        desktopUrl,
      );

      updateModalShare({
        shareTitle,
        shareUrl,
        shareHashtag,
      });

      modal.classList.add('is-open');

      modal.setAttribute(
        'aria-hidden',
        'false',
      );

      document.body.classList.add(
        'dotd-digital-modal-open',
      );

      window.requestAnimationFrame(() => {
        modal
          .querySelector(
            '.dotd-digital__modal-close',
          )
          ?.focus();
      });
    };

    const openButtons = [
      ...section.querySelectorAll(
        '[data-dotd-digital-open]',
      ),
    ];

    const findOpenTriggerByShareKey = (
      shareKey,
    ) => {
      if (!shareKey) return null;

      return (
        openButtons.find(
          (button) =>
            button.dataset.shareKey ===
              shareKey &&
            !button.closest(
              '[data-dotd-digital-clone]',
            ),
        ) ||
        openButtons.find(
          (button) =>
            button.dataset.shareKey ===
            shareKey,
        ) ||
        null
      );
    };

    const openModalFromShareHash = () => {
      const hash = window.location.hash;

      if (!hash) return;

      const shareKey = decodeURIComponent(
        hash.replace(/^#/, ''),
      ).trim();

      if (!shareKey) return;

      const trigger =
        findOpenTriggerByShareKey(
          shareKey,
        );

      if (!trigger) return;

      openModal(trigger, {
        fromShareHash: true,
      });
    };

    const handleKeyDown = (event) => {
      if (isModalOpen()) {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeModal();
          return;
        }

        if (
          event.key !== 'Tab' ||
          !modal
        ) {
          return;
        }

        const focusable = [
          ...modal.querySelectorAll(
            [
              'button:not([disabled]):not([hidden])',
              'a[href]:not([hidden])',
              '[tabindex]:not([tabindex="-1"])',
            ].join(','),
          ),
        ].filter(
          (element) =>
            element.offsetParent !== null,
        );

        if (!focusable.length) return;

        const first = focusable[0];
        const last =
          focusable[
            focusable.length - 1
          ];

        if (
          event.shiftKey &&
          document.activeElement === first
        ) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          document.activeElement === last
        ) {
          event.preventDefault();
          first.focus();
        }

        return;
      }

      if (
        !slider?.contains(
          document.activeElement,
        )
      ) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveBy(-1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveBy(1);
      }
    };

    const handleBreakpointChange = () => {
      if (
        !track ||
        !viewport ||
        !slideCount
      ) {
        return;
      }

      pendingDesktopJump = null;

      if (mobileSettleTimer !== null) {
        window.clearTimeout(
          mobileSettleTimer,
        );

        mobileSettleTimer = null;
      }

      if (mobileQuery.matches) {
        track.style.removeProperty(
          'transform',
        );

        track.style.removeProperty(
          'transition',
        );

        currentRenderedIndex =
          getRenderedIndexForLogical(
            currentIndex,
          );

        window.requestAnimationFrame(() => {
          scrollToRendered(
            currentRenderedIndex,
            false,
          );
        });
      } else {
        viewport.scrollTo({
          left: 0,
          behavior: 'auto',
        });

        currentRenderedIndex =
          getRenderedIndexForLogical(
            currentIndex,
          );

        setTrackPosition(
          currentRenderedIndex,
          false,
        );
      }

      updateInterface(currentIndex);
    };

    listen(
      previousButton,
      'click',
      () => moveBy(-1),
    );

    listen(
      nextButton,
      'click',
      () => moveBy(1),
    );

    listen(
      viewport,
      'scroll',
      requestMobileIndexUpdate,
      { passive: true },
    );

    listen(
      track,
      'transitionend',
      handleTrackTransitionEnd,
    );

    listen(
      document,
      'keydown',
      handleKeyDown,
    );

    listen(
      mobileQuery,
      'change',
      handleBreakpointChange,
    );

    openButtons.forEach((button) => {
      listen(
        button,
        'click',
        () =>
          openModal(button, {
            fromShareHash: false,
          }),
      );
    });

    closeButtons.forEach((button) => {
      listen(
        button,
        'click',
        () => closeModal(),
      );
    });

    updateInterface(0);
    handleBreakpointChange();
    openModalFromShareHash();

    section.setAttribute(
      'data-dotd-digital-initialized',
      'true',
    );

    controllers.set(section, {
      destroy() {
        destroyed = true;

        closeModal({
          restoreFocus: false,
        });

        cleanups
          .splice(0)
          .forEach((cleanup) => {
            cleanup();
          });

        if (scrollFrame !== null) {
          window.cancelAnimationFrame(
            scrollFrame,
          );
        }

        if (mobileSettleTimer !== null) {
          window.clearTimeout(
            mobileSettleTimer,
          );
        }

        firstClone?.remove();
        lastClone?.remove();

        track?.style.removeProperty(
          'transform',
        );

        track?.style.removeProperty(
          'transition',
        );

        section.removeAttribute(
          'data-dotd-digital-initialized',
        );

        controllers.delete(section);
      },
    });
  };

  const initialize = (
    scope = document,
  ) => {
    const sections = [];

    if (
      scope?.matches?.(
        '[data-dotd-digital]',
      )
    ) {
      sections.push(scope);
    }

    scope
      ?.querySelectorAll?.(
        '[data-dotd-digital]',
      )
      .forEach((section) => {
        sections.push(section);
      });

    sections.forEach(createController);
  };

  const destroy = (scope) => {
    const sections = [];

    if (
      scope?.matches?.(
        '[data-dotd-digital]',
      )
    ) {
      sections.push(scope);
    }

    scope
      ?.querySelectorAll?.(
        '[data-dotd-digital]',
      )
      .forEach((section) => {
        sections.push(section);
      });

    sections.forEach((section) => {
      controllers
        .get(section)
        ?.destroy();
    });
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize(),
      { once: true },
    );
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialize(event.target);
    },
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroy(event.target);
    },
  );
})();